import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Portfolio } from '../types';
import { loadPortfolio } from '../utils/apiStorage';
import { calculateInvestmentSummary, formatCurrency, formatPercentage } from '../utils/calculations';
import Sidebar from './Sidebar';
import MobileHeader from './MobileHeader';
import MobileBottomNav from './MobileBottomNav';
import Overview from './Overview';
import Family from './Family';
import FinancialModel from './FinancialModel';
import IncomeExpenses from './IncomeExpenses';
import CASUpload from './CASUpload';
import authService, { User } from '../services/auth';

interface DashboardProps {
  user: User;
  onLogout: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const [portfolio, setPortfolio] = useState<Portfolio>({ familyMembers: [], investments: [], expenses: [], incomes: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        console.log('Loading portfolio data...');
        const loadedPortfolio = await loadPortfolio();
        console.log('Portfolio loaded:', {
          familyMembers: loadedPortfolio.familyMembers.length,
          investments: loadedPortfolio.investments.length,
          expenses: loadedPortfolio.expenses?.length || 0,
          incomes: loadedPortfolio.incomes?.length || 0,
        });
        // Ensure expenses and incomes are always arrays
        setPortfolio({
          ...loadedPortfolio,
          expenses: loadedPortfolio.expenses || [],
          incomes: loadedPortfolio.incomes || [],
        });
      } catch (error) {
        console.error('Error loading portfolio:', error);
        // Check if it's an auth error
        if (error instanceof Error && (error.message.includes('401') || error.message.includes('Unauthorized'))) {
          console.log('Authentication error, logging out...');
          authService.logout();
          onLogout();
          return;
        }
        // Fallback to empty portfolio for other errors
        console.log('Using empty portfolio due to error');
        setPortfolio({ familyMembers: [], investments: [], expenses: [], incomes: [] });
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [onLogout]);

  const summary = calculateInvestmentSummary(portfolio.investments);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-x-hidden">
      {/* Desktop Sidebar */}
      <Sidebar user={user} onLogout={onLogout} />
      
      {/* Mobile Header */}
      <MobileHeader user={user} onLogout={onLogout} />
      
      <div className="flex-1 flex flex-col md:ml-64 w-0 min-w-0">
        {/* Desktop Header */}
        <header className="hidden md:block fixed top-0 right-0 left-64 bg-white shadow-sm border-b border-gray-200 z-10">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Financial Planner</h1>
                <p className="text-gray-600">Manage and visualize your family's investments</p>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-sm text-gray-500">Total Invested</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(summary.totalInvested)}
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm text-gray-500">Current Value</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(summary.totalCurrentValue)}
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="text-sm text-gray-500">Growth</div>
                  <div className={`text-lg font-semibold ${
                    summary.totalGrowth >= 0 ? 'text-success-600' : 'text-danger-600'
                  }`}>
                    {formatPercentage(summary.growthPercentage)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden md:mt-24 mt-16 pb-20 md:pb-0 w-full max-w-full">
          <Routes>
            <Route path="/overview" element={<Overview portfolio={portfolio} setPortfolio={setPortfolio} />} />
            <Route path="/family" element={<Family portfolio={portfolio} setPortfolio={setPortfolio} user={user} />} />
            <Route path="/family-members" element={<Navigate to="/family" replace />} />
            <Route path="/investments" element={<Navigate to="/family" replace />} />
            <Route path="/projections" element={<FinancialModel portfolio={portfolio} />} />
            <Route path="/financial-model" element={<Navigate to="/projections" replace />} />
            <Route path="/income-expenses" element={<IncomeExpenses portfolio={portfolio} setPortfolio={setPortfolio} />} />
            <Route path="/expenses" element={<Navigate to="/income-expenses" replace />} />
            <Route path="/income" element={<Navigate to="/income-expenses" replace />} />
            <Route path="/cas-upload" element={<CASUpload portfolio={portfolio} setPortfolio={setPortfolio} />} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </main>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default Dashboard;

